import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StarSpinner } from "@/components/StarSpinner";

const EMPTY = {
  client_name: "",
  client_email: "",
  business_name: "",
  engagement_date: "",
  matter_title: "",
  scope_items_text: "",   // one per line, split on save
  fee_arrangement: "",
  term: "Engagement is effective as of the date above and continues until the scope of representation is completed or either party elects to terminate in writing with reasonable notice.",
  salutation: "",
  closing_note: "",
};

export default function AdminEngagementLetter() {
  const [form, setForm] = useState(EMPTY);
  const [generating, setGenerating] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.matter_title || !form.fee_arrangement) {
      toast.error("Client name, email, matter title, and fee arrangement are required");
      return;
    }
    setGenerating(true);
    try {
      const scope_items = form.scope_items_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post(
        "/admin/engagement-letter.pdf",
        {
          client_name: form.client_name,
          client_email: form.client_email,
          business_name: form.business_name || null,
          engagement_date: form.engagement_date || null,
          matter_title: form.matter_title,
          scope_items,
          fee_arrangement: form.fee_arrangement,
          term: form.term,
          salutation: form.salutation || null,
          closing_note: form.closing_note || null,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = form.client_email.toLowerCase().replace("@", "-at-").replace(/\./g, "-");
      a.download = `LSC-Engagement-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Engagement letter downloaded");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const reset = () => setForm(EMPTY);

  return (
    <div className="mt-10" data-testid="admin-engagement-tab">
      <div className="hairline-bottom pb-6 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="kicker">Engagement Letter</div>
          <h2 className="mt-3 font-serif text-3xl text-navy">
            Compose a Letter of Engagement
          </h2>
          <p className="mt-3 max-w-2xl text-slate2 font-light">
            Draft a formal welcome and scope letter for a new client. Rendered on the firm&apos;s
            letterhead with the bronze star seal, signature block, and countersignature line.
            Downloads as a print-ready PDF.
          </p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/welcome-kit.pdf`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost inline-flex items-center gap-2 shrink-0"
          data-testid="download-welcome-kit"
          title="Standing 4-panel print piece — hand at first meetings"
        >
          <span className="text-brass text-lg leading-none">&#9733;</span>
          Download Welcome Kit (Print PDF)
        </a>
      </div>

      <form onSubmit={generate} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 max-w-5xl">
        <Field label="Client Name" required>
          <input type="text" required className="field" value={form.client_name}
                 onChange={set("client_name")} data-testid="engagement-client-name" />
        </Field>
        <Field label="Client Email" required>
          <input type="email" required className="field" value={form.client_email}
                 onChange={set("client_email")} data-testid="engagement-client-email" />
        </Field>

        <Field label="Business of Record">
          <input type="text" className="field" placeholder="Doe Holdings LLC"
                 value={form.business_name} onChange={set("business_name")}
                 data-testid="engagement-business-name" />
        </Field>
        <Field label="Engagement Date">
          <input type="date" className="field" value={form.engagement_date}
                 onChange={set("engagement_date")} data-testid="engagement-date" />
        </Field>

        <Field label="Matter Title" required span={2}>
          <input type="text" required className="field"
                 placeholder="Formation of Doe Holdings LLC and initial IP portfolio protection"
                 value={form.matter_title} onChange={set("matter_title")}
                 data-testid="engagement-matter-title" />
        </Field>

        <Field label="Scope of Representation" span={2} hint="One item per line">
          <textarea rows={6} className="field font-cormorant"
                    placeholder={"Series LLC formation under Texas Business Organizations Code\nDraft operating agreement and founder equity structure\nFederal trademark application for the LONE STAR HERITAGE mark"}
                    value={form.scope_items_text} onChange={set("scope_items_text")}
                    data-testid="engagement-scope" />
        </Field>

        <Field label="Fee Arrangement" required span={2}>
          <textarea rows={3} required className="field"
                    placeholder="Fixed engagement fee of $12,500 payable in two installments…"
                    value={form.fee_arrangement} onChange={set("fee_arrangement")}
                    data-testid="engagement-fee" />
        </Field>

        <Field label="Term of Engagement" required span={2}>
          <textarea rows={3} required className="field"
                    value={form.term} onChange={set("term")}
                    data-testid="engagement-term" />
        </Field>

        <Field label="Custom Salutation" span={2} hint='Optional — defaults to "Dear [First Name],"'>
          <input type="text" className="field" placeholder='Dear Jane,'
                 value={form.salutation} onChange={set("salutation")}
                 data-testid="engagement-salutation" />
        </Field>

        <Field label="Closing Note" span={2} hint="Optional — overrides the default closing sentence.">
          <textarea rows={2} className="field"
                    value={form.closing_note} onChange={set("closing_note")}
                    data-testid="engagement-closing" />
        </Field>

        <div className="md:col-span-2 hairline-top pt-6 flex flex-wrap items-center justify-end gap-4">
          <button type="button" onClick={reset} className="btn-ghost" data-testid="engagement-reset">
            Clear form
          </button>
          <button
            type="submit"
            disabled={generating}
            className="btn-primary disabled:opacity-70 inline-flex items-center gap-3"
            data-testid="engagement-generate"
          >
            {generating ? (
              <>
                <StarSpinner size="sm" label="" />
                <span>Preparing letter…</span>
              </>
            ) : (
              <span>Generate Engagement Letter (PDF)</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, hint, span, children }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`}>
      <span className="kicker text-[0.62rem]">
        {label} {required && <span className="text-oxblood">*</span>}
      </span>
      {hint && <span className="ml-2 font-cormorant italic text-slate2 text-xs normal-case tracking-normal">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}
